import { Link } from "react-router-dom";

export default function SeoIntroSection() {
  return (
    <section className="relative py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-6 lg:px-12 max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Left: Label + Image */}
          <div className="lg:w-1/4 shrink-0">
            <span className="text-xs uppercase tracking-widest text-secondary-500 font-label">/ About This Hub</span>
            <div className="mt-8 rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Minimalist%20editorial%20product%20photography%20of%20a%20beautifully%20organized%20desk%20with%20laptop%20displaying%20spreadsheet%20data%20alongside%20curated%20fashion%20items%20shoes%20and%20accessories%20on%20a%20warm%20cream%20surface%20with%20soft%20natural%20window%20light%2C%20professional%20workspace%20aesthetic%2C%20neutral%20tones%2C%20clean%20composition&width=600&height=750&seq=seo-intro-img-01&orientation=portrait"
                alt="Mulebuy spreadsheet workspace with product curation"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Right: Content */}
          <div className="lg:w-3/4">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-medium text-foreground-950 leading-snug mb-8">
              Your Complete <strong>Mulebuy Spreadsheet</strong> Resource — Curated, Verified, and Updated Daily
            </h2>

            <div className="prose-custom space-y-5 text-foreground-700 text-base leading-relaxed">
              <p>
                Navigating the world of product sourcing can be overwhelming without the right tools. A quality{" "}
                <strong>mulebuy spreadsheet</strong> serves as your compass — organizing thousands of products across
                categories like shoes, hoodies, electronics, and accessories into a single accessible directory.
                Whether you are a seasoned dropshipper or just starting your e-commerce journey, having a reliable
                spreadsheet hub makes all the difference between guesswork and informed decisions.
              </p>
              <p>
                Our platform aggregates the most comprehensive <strong>mulebuy spreadsheet</strong> collections available
                online. Each listing is verified for accuracy, categorized intuitively, and updated on a regular schedule
                to ensure you never work with outdated data. From trending sneaker releases to hidden-gem accessories,
                every spreadsheet in our directory has been vetted against strict quality criteria.
              </p>
              <p>
                What sets this hub apart is the emphasis on user experience. Instead of digging through scattered forums
                and unreliable Telegram channels, you get a clean, organized interface where every{" "}
                <strong>mulebuy spreadsheet</strong> entry includes a quality score, update frequency, and detailed notes.
                The built-in category navigation lets you jump directly to shoes, jackets, bags, perfumes, or any of the
                fourteen curated sections.
              </p>
              <p>
                We believe that access to quality sourcing data should be straightforward. That is why this entire
                directory is freely accessible, with no paywalls, no forced sign-ups, and no hidden fees. Dive into
                the category grid below, explore our featured spreadsheet comparisons, or{" "}
                <Link to="/mulebuy-spreadsheet-guide" className="text-accent-600 hover:text-accent-700 underline underline-offset-2">
                  read our complete guide
                </Link>{" "}
                on maximizing your <strong>mulebuy spreadsheet</strong> workflow.
              </p>
            </div>

            {/* Trust badges with images */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 pt-8 border-t border-background-200/70">
              <div className="bg-background-50 rounded-xl p-4 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Minimalist%20product%20photography%20of%20a%20calendar%20icon%20concept%20on%20a%20clean%20cream%20surface%20with%20soft%20lighting%2C%20update%20symbol%2C%20neutral%20tones%2C%20simple%20composition&width=100&height=100&seq=badge-daily-01&orientation=squarish"
                    alt="Daily updates"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-xs font-medium text-foreground-700">Updated Daily</span>
              </div>
              <div className="bg-background-50 rounded-xl p-4 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Clean%20product%20photography%20of%20a%20verified%20checkmark%20shield%20concept%20on%20a%20cream%20surface%20with%20soft%20lighting%2C%20trust%20symbol%2C%20neutral%20tones%2C%20simple%20composition&width=100&height=100&seq=badge-verified-02&orientation=squarish"
                    alt="Verified sources"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-xs font-medium text-foreground-700">Verified Sources</span>
              </div>
              <div className="bg-background-50 rounded-xl p-4 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Minimalist%20product%20photography%20of%20a%20heart%20icon%20concept%20on%20a%20clean%20cream%20surface%20with%20soft%20lighting%2C%20community%20symbol%2C%20neutral%20tones%2C%20simple%20composition&width=100&height=100&seq=badge-trusted-03&orientation=squarish"
                    alt="Trusted community"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-xs font-medium text-foreground-700">Trusted Community</span>
              </div>
              <div className="bg-background-50 rounded-xl p-4 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Clean%20product%20photography%20of%20a%20lightbulb%20icon%20concept%20on%20a%20cream%20surface%20with%20soft%20lighting%2C%20easy%20symbol%2C%20neutral%20tones%2C%20simple%20composition&width=100&height=100&seq=badge-beginner-04&orientation=squarish"
                    alt="Beginner friendly"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-xs font-medium text-foreground-700">Beginner Friendly</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}